import { isAdminRole } from '../utils/roles';
import { API_BASE_URL } from './config';

class CompanyService {
  private baseURL = API_BASE_URL;

  private async request(endpoint: string, options: RequestInit = {}) {
    const token = localStorage.getItem('token');
    const url = `${this.baseURL}${endpoint}`;
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Get current user's company information
  async getCurrentCompany() {
    try {
      // Since there's no dedicated company endpoint, we'll get user info which includes company
      const userResponse = await this.request('/auth/me');
      const user = userResponse?.user || userResponse;
      if (user && user.company) {
        return user.company;
      }
      // If no company on user (common for super admins), return null instead of throwing
      return null;
    } catch (error) {
      console.error('Error fetching current company:', error);
      // Return null so callers can decide how to proceed (e.g., admins fetching all companies)
      return null;
    }
  }

  // Get company statistics
  async getCompanyStats() {
    try {
      // Get project stats for the company
      const projectStats = await this.request('/projects/stats/dashboard');
      
      // Get user count (assuming there's a users endpoint)
      let userCount = 0;
      try {
        const usersResponse = await this.request('/users');
        userCount = Array.isArray(usersResponse.users) ? usersResponse.users.length : 0;
      } catch (error) {
        console.warn('Could not fetch user count:', error);
      }

      return {
        totalProjects: projectStats.totalProjects || 0,
        activeProjects: projectStats.activeProjects || 0,
        completedProjects: projectStats.completedProjects || 0,
        totalUsers: userCount,
        ...projectStats
      };
    } catch (error) {
      console.error('Error fetching company stats:', error);
      return {
        totalProjects: 0,
        activeProjects: 0,
        completedProjects: 0,
        totalUsers: 0
      };
    }
  }

  // Transform backend company data to frontend format
  private transformCompanyData(backendCompany: any, stats: any = {}) {
    return {
      // Always expose a stable `id` string and `name` string
      id: String(backendCompany._id || backendCompany.id || ''),
      name: String(backendCompany.name || backendCompany.legalName || ''),
      legalName: backendCompany.legalName || backendCompany.name || '',
      taxId: backendCompany.businessInfo?.taxId || '',
      industry: backendCompany.industry || 'Construction',
      address: {
        street: backendCompany.address?.street || '',
        city: backendCompany.address?.city || '',
        state: backendCompany.address?.state || '',
        zipCode: backendCompany.address?.zipCode || '',
        country: backendCompany.address?.country || 'United States',
      },
      contact: {
        phone: backendCompany.contact?.primaryContact?.phone || '',
        email: backendCompany.contact?.primaryContact?.email || '',
        website: backendCompany.businessInfo?.website || '',
      },
      logo: backendCompany.branding?.logo?.url,
      status: this.mapStatus(backendCompany.status),
      subscription: {
        plan: backendCompany.subscription?.plan || 'Free',
        status: this.mapSubscriptionStatus(backendCompany.subscription?.status),
        expiryDate: backendCompany.subscription?.endDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        users: stats.totalUsers || backendCompany.subscription?.usage?.users || 0,
        maxUsers: backendCompany.subscription?.features?.maxUsers || 5,
      },
      settings: {
        timezone: backendCompany.settings?.timezone || 'America/New_York',
        currency: backendCompany.settings?.currency || 'USD',
        language: backendCompany.settings?.language || 'en',
        dateFormat: backendCompany.settings?.dateFormat || 'MM/DD/YYYY',
        notifications: backendCompany.settings?.notifications?.email?.enabled !== false,
        backupEnabled: true, // Default to true
        twoFactorAuth: backendCompany.settings?.security?.twoFactorRequired || false,
      },
      createdDate: backendCompany.createdAt || new Date().toISOString(),
      lastUpdated: backendCompany.updatedAt || new Date().toISOString(),
      totalUsers: stats.totalUsers || 0,
      totalProjects: stats.totalProjects || 0,
      storageUsed: (backendCompany.subscription?.usage?.storage || 0) / (1024 * 1024 * 1024), // Convert bytes to GB
      storageLimit: (backendCompany.subscription?.features?.maxStorage || 1073741824) / (1024 * 1024 * 1024), // Convert bytes to GB
    };
  }

  private mapStatus(backendStatus: string): 'active' | 'inactive' | 'suspended' {
    switch (backendStatus?.toLowerCase()) {
      case 'active':
        return 'active';
      case 'suspended':
        return 'suspended';
      case 'inactive':
      case 'pending':
      default:
        return 'inactive';
    }
  }

  private mapSubscriptionStatus(backendStatus: string): 'active' | 'expired' | 'trial' {
    switch (backendStatus?.toLowerCase()) {
      case 'active':
        return 'active';
      case 'trial':
        return 'trial';
      case 'cancelled':
      case 'past due':
      case 'suspended':
      default:
        return 'expired';
    }
  }

  // Get all companies (for admin users)
  async getAllCompanies() {
    try {
      // Determine user role to choose strategy
      const me = await this.request('/auth/me');
      const user = me?.user || me;
      const role = user?.role;
      const isAdmin = isAdminRole(role);

      if (isAdmin) {
        // Admins can list all companies via backend endpoint
        const companiesResponse = await this.request('/companies');
        const items = Array.isArray(companiesResponse?.data) ? companiesResponse.data : (Array.isArray(companiesResponse) ? companiesResponse : []);
        const stats = await this.getCompanyStats();
        return items.map((c: any) => this.transformCompanyData(c, stats));
      }

      // Non-admins: fall back to current user's company
      const currentCompany = await this.getCurrentCompany();
      if (!currentCompany) {
        return [];
      }
      const stats = await this.getCompanyStats();
      return [this.transformCompanyData(currentCompany, stats)];
    } catch (error) {
      console.error('Error fetching companies:', error);
      return [];
    }
  }

  // Update company information
  async updateCompany(companyId: string, updateData: any) {
    try {
      // Since there's no company update endpoint, we'll simulate success
      console.warn('Company update not implemented - no backend endpoint available');
      return { success: true, message: 'Company update functionality not yet implemented' };
    } catch (error) {
      console.error('Error updating company:', error);
      throw error;
    }
  }

  // Create new company (for admin users)
  async createCompany(companyData: any) {
    try {
      // Map frontend form data to backend schema
      const payload = {
        name: companyData.companyName,
        legalName: companyData.legalName,
        type: 'Other',
        industry: companyData.industry || 'Commercial Construction',
        contact: {
          primaryContact: {
            name: companyData.companyName,
            email: companyData.primaryEmail,
            phone: companyData.primaryPhone,
          },
        },
        address: {
          street: companyData.headquarters?.address,
          city: companyData.headquarters?.city,
          state: companyData.headquarters?.state,
          zipCode: companyData.headquarters?.zipCode,
          country: companyData.headquarters?.country || 'United States',
        },
        businessInfo: {
          taxId: companyData.taxId,
          licenseNumber: companyData.businessLicense,
          insuranceProvider: companyData.insuranceProvider,
          insurancePolicyNumber: companyData.insurancePolicyNumber,
          bondingCompany: companyData.bondingCompany,
          bondAmount: companyData.bondAmount ? Number(companyData.bondAmount) : undefined,
          website: companyData.website,
          foundedYear: companyData.foundedYear ? Number(companyData.foundedYear) : undefined,
        },
        branding: {
          logo: { url: companyData.branding?.logoUrl },
          favicon: { url: companyData.branding?.faviconUrl },
          colors: {
            primary: companyData.branding?.primaryColor,
            secondary: companyData.branding?.secondaryColor,
          },
          customCss: companyData.branding?.customCss,
        },
        settings: {
          timezone: companyData.timezone,
          currency: companyData.currency,
          fiscalYearStart: companyData.fiscalYearStart,
          workingDays: companyData.workingDays,
          workingHours: {
            start: companyData.workingHours?.start,
            end: companyData.workingHours?.end,
          },
          security: {
            passwordPolicy: {
              minLength: companyData.security?.passwordPolicy?.minLength,
              requireUppercase: companyData.security?.passwordPolicy?.requireUppercase,
              requireLowercase: companyData.security?.passwordPolicy?.requireLowercase,
              requireNumbers: companyData.security?.passwordPolicy?.requireNumbers,
              requireSpecialChars: companyData.security?.passwordPolicy?.requireSpecialChars,
              expiryDays: companyData.security?.passwordPolicy?.expirationDays,
            },
            twoFactorRequired: companyData.security?.twoFactorAuth,
            sessionTimeout: companyData.security?.sessionTimeout,
            ipWhitelist: companyData.security?.ipWhitelist || [],
          },
          notifications: {
            email: { enabled: companyData.notifications?.emailNotifications },
            sms: { enabled: companyData.notifications?.smsNotifications },
            push: { enabled: companyData.notifications?.pushNotifications },
          },
        },
        departments: Array.isArray(companyData.departments)
          ? companyData.departments.map((d: any) => ({ name: d.name, description: d.description }))
          : [],
      };

      const result = await this.request('/companies', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return result;
    } catch (error) {
      console.error('Error creating company:', error);
      throw error;
    }
  }

  // Delete company (for admin users)
  async deleteCompany(companyId: string) {
    try {
      // Since there's no company deletion endpoint, we'll simulate success
      console.warn('Company deletion not implemented - no backend endpoint available');
      return { success: true, message: 'Company deletion functionality not yet implemented' };
    } catch (error) {
      console.error('Error deleting company:', error);
      throw error;
    }
  }
}

export const companyService = new CompanyService();
export default companyService;